import React, { useState } from "react";
import { CheckCircle, ChevronDown, ChevronUp, AlertTriangle, Lightbulb } from "lucide-react";
import businessConfig from "../config/businessConfig";

// ── Expert content per service ────────────────────────────────
const SERVICE_DETAILS = {
  "dry-cleaning": {
    why: "Dry cleaning uses professional-grade solvents that dissolve oils, grease and stubborn stains that water simply cannot remove. For suits, formal wear, silk, wool and delicate fabrics — dry cleaning is not optional, it is essential. Washing these items at home risks shrinkage, colour bleed and permanent fabric damage.",
    didYouKnow: "Most fabric damage happens at home. Over 60% of garment complaints we receive are from customers who first tried washing delicate items themselves before bringing them to us.",
    steps: [
      "Garment inspection — we identify fabric type and stain locations",
      "Pre-treatment of all stains with specialist solvents",
      "Professional dry cleaning machine cycle",
      "Hand finishing and pressing to restore original shape",
      "Final quality inspection before packaging",
    ],
    mistake: "Never put a 'Dry Clean Only' label garment in a washing machine — even on a gentle cycle. The agitation alone can permanently distort the fabric structure.",
    quote: "\"We treat every garment as if it were our own. A suit is an investment — we protect it.\" — Our Head Cleaner",
    color: "indigo",
  },
  "wash-fold": {
    why: "Everyday laundry takes more time than most people realise — sorting, washing, drying, folding can easily take 2 to 3 hours per week. Our Wash & Fold service gives you that time back. Every item is washed at the correct temperature, dried properly and folded neatly — no more wrinkled clothes pulled from the bottom of a laundry basket.",
    didYouKnow: "Washing clothes at the wrong temperature is the number one cause of shrinkage and colour fading. Our machines are calibrated precisely for each fabric load.",
    steps: [
      "Sorting by colour, fabric type and wash temperature",
      "Commercial grade washing at correct temperature",
      "Professional dryer cycle — no over-drying",
      "Neat folding and organising by garment type",
      "Packaged and ready for delivery",
    ],
    mistake: "Overloading a home washing machine leaves detergent residue in clothes and reduces cleaning effectiveness. Our commercial machines handle each load with room to breathe.",
    quote: "\"Clean laundry should not be a weekend chore. We take it off your hands completely.\" — Founder",
    color: "blue",
  },
  "alterations": {
    why: "A perfectly fitted garment looks entirely different from one that is slightly off. Whether a hem is too long, a waist too wide, or a sleeve needs reshaping — expert alterations transform how your clothes look and feel on your body.",
    didYouKnow: "Most people own clothes they never wear simply because they do not fit quite right. A small alteration can bring those garments back into regular use.",
    steps: [
      "Fitting consultation — we assess what needs adjusting",
      "Precise measurements and pinning",
      "Expert tailoring by our skilled team",
      "Quality check fit assessment",
      "Pressed and packaged for delivery",
    ],
    mistake: "Never cut or attempt to alter a garment yourself. An incorrect cut cannot be undone — always bring it to a professional.",
    quote: "\"The difference between a garment that fits and one that was made for you is everything.\" — Senior Tailor",
    color: "purple",
  },
  "press-only": {
    why: "A perfectly pressed shirt or dress makes an enormous difference to how professional and confident you look. Whether you have a job interview, a business meeting or a special occasion — wrinkled clothes send the wrong message. Our pressing service delivers crisp, sharp results that a home iron simply cannot match.",
    didYouKnow: "Professional steam presses operate at much higher temperatures and pressure than home irons — removing wrinkles in a single pass that would take several passes at home.",
    steps: [
      "Garment inspection for any existing stains or damage",
      "Fabric-appropriate temperature setting",
      "Professional steam pressing — collar, cuffs and panels",
      "Hanging immediately to preserve the press",
      "Protective garment bag for delivery",
    ],
    mistake: "Pressing clothes that are too dry can scorch the fabric. Always lightly dampen garments first — or better yet, let our professionals handle it.",
    quote: "\"The difference between good and great is often just a perfect press.\" — Senior Pressing Specialist",
    color: "green",
  },
  "duvet-wash": {
    why: "Duvets and bedding absorb sweat, skin cells, dust mites and allergens over time. A dirty duvet does not just smell — it actively affects your sleep quality and can trigger allergies and respiratory issues. Most home washing machines are too small to clean a duvet properly, leaving it damp inside which leads to mould growth.",
    didYouKnow: "The average duvet doubles in weight over 2 years from accumulated sweat, dead skin and dust mites — even with a duvet cover. Professional washing removes all of this completely.",
    steps: [
      "Size and fill type assessment — down, synthetic or wool",
      "Large capacity commercial washing — full saturation",
      "Extended drying cycle — ensuring complete dryness inside",
      "Loft restoration — fluffing to restore original thickness",
      "Final inspection and hygienic packaging",
    ],
    mistake: "Never put a damp duvet back on a bed. Incomplete drying inside a duvet causes mould to grow inside the filling — which is invisible but harmful to health.",
    quote: "\"A clean duvet is not a luxury — it is a health necessity. We make it easy and affordable.\" — Operations Manager",
    color: "purple",
  },
};

const COLOR_MAP = {
  indigo: { bg:"bg-indigo-50",  border:"border-indigo-600", badge:"bg-indigo-600",  text:"text-indigo-700",  btn:"bg-indigo-600 hover:bg-indigo-700",  light:"bg-indigo-100" },
  blue:   { bg:"bg-blue-50",    border:"border-blue-500",   badge:"bg-blue-500",    text:"text-blue-700",    btn:"bg-blue-600 hover:bg-blue-700",       light:"bg-blue-100"   },
  green:  { bg:"bg-green-50",   border:"border-green-500",  badge:"bg-green-500",   text:"text-green-700",   btn:"bg-green-600 hover:bg-green-700",     light:"bg-green-100"  },
  red:    { bg:"bg-red-50",     border:"border-red-500",    badge:"bg-red-500",     text:"text-red-700",     btn:"bg-red-600 hover:bg-red-700",         light:"bg-red-100"    },
  purple: { bg:"bg-purple-50",  border:"border-purple-500", badge:"bg-purple-500",  text:"text-purple-700",  btn:"bg-purple-600 hover:bg-purple-700",   light:"bg-purple-100" },
};

export default function ServiceCards({ setBookOpen }) {
  const [expandedId, setExpandedId] = useState(null);

  // Only show active services
  const services = businessConfig.services.filter(s => s.active);

  function toggle(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  return (
    <div className="space-y-4">
      {services.map((s) => {
        const isExpanded = expandedId === s.id;
        const details    = SERVICE_DETAILS[s.id] || {};

        // ── Most Popular: read from businessConfig.popular flag ──
        const isPopular  = s.popular === true;
        // ── Fastest: Press Only is quickest at 12 hours ──
        const isFastest  = s.id === "press-only";

        const turnaround = s.duration || "24 hours";
        const colors     = COLOR_MAP[details.color || "indigo"];

        return (
          <div
            key={s.id}
            className={`relative rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
              isExpanded ? colors.border + " shadow-xl" : "border-gray-100 hover:border-gray-300 shadow-sm hover:shadow-md"
            }`}
          >
            {/* ── Card Header — always visible ── */}
            <button
              onClick={() => toggle(s.id)}
              className="w-full text-left p-5 md:p-6 flex items-center gap-4 focus:outline-none"
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-2xl ${colors.light} flex items-center justify-center text-3xl shrink-0 transition-transform duration-300 ${isExpanded ? "scale-110" : ""}`}>
                {s.icon}
              </div>

              {/* Name + desc */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-black text-lg text-gray-900">{s.name}</h3>
                  {isPopular && (
                    <span className={`${colors.badge} text-white text-xs font-bold px-2.5 py-0.5 rounded-full`}>
                      ⭐ Most Popular
                    </span>
                  )}
                  {isFastest && (
                    <span className="bg-green-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
                      ⚡ Fastest
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-1">
                  {s.desc}
                </p>
              </div>

              {/* Right side — turnaround + expand icon */}
              <div className="flex items-center gap-3 shrink-0">
                <span className={`hidden sm:block text-xs font-bold px-3 py-1.5 rounded-xl ${
                  turnaround === "12 hours"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : turnaround === "24 hours"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                }`}>
                  ⏱ {turnaround}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isExpanded ? colors.light + " " + colors.text : "bg-gray-100 text-gray-500"
                }`}>
                  {isExpanded ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
                </div>
              </div>
            </button>

            {/* ── Expanded content ── */}
            {isExpanded && (
              <div className={`px-5 md:px-6 pb-6 border-t ${colors.bg}`}>
                <div className="pt-5 grid md:grid-cols-2 gap-6">

                  {/* Left column */}
                  <div className="space-y-5">

                    {/* Why you need this */}
                    <div>
                      <h4 className={`font-black text-sm uppercase tracking-wide ${colors.text} mb-2 flex items-center gap-2`}>
                        <span>💡</span> Why You Need This Service
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {details.why}
                      </p>
                    </div>

                    {/* Did you know */}
                    <div className={`${colors.light} rounded-2xl p-4`}>
                      <h4 className={`font-black text-xs uppercase tracking-wide ${colors.text} mb-2 flex items-center gap-1.5`}>
                        <Lightbulb size={13}/> Did You Know?
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {details.didYouKnow}
                      </p>
                    </div>

                    {/* Expert quote */}
                    <div className="border-l-4 border-gray-300 pl-4">
                      <p className="text-gray-500 text-sm italic leading-relaxed">
                        {details.quote}
                      </p>
                    </div>

                  </div>

                  {/* Right column */}
                  <div className="space-y-5">

                    {/* What our experts do */}
                    <div>
                      <h4 className={`font-black text-sm uppercase tracking-wide ${colors.text} mb-3 flex items-center gap-2`}>
                        <span>✅</span> What Our Experts Do
                      </h4>
                      <div className="space-y-2">
                        {details.steps?.map((step, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className={`w-5 h-5 rounded-full ${colors.badge} text-white flex items-center justify-center text-xs font-black shrink-0 mt-0.5`}>
                              {j + 1}
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Common mistake */}
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                      <h4 className="font-black text-xs uppercase tracking-wide text-red-600 mb-2 flex items-center gap-1.5">
                        <AlertTriangle size={13}/> Common Mistake to Avoid
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {details.mistake}
                      </p>
                    </div>

                    {/* Included */}
                    <div className="bg-white rounded-2xl p-4 border border-gray-100">
                      <h4 className="font-black text-xs uppercase tracking-wide text-gray-500 mb-3">
                        Every Order Includes
                      </h4>
                      {[
                        "Free pickup from your door (25pc min)",
                        "Expert professional treatment",
                        "Quality check before delivery",
                        "Free delivery back to you",
                        "100% satisfaction guarantee",
                      ].map((item, j) => (
                        <div key={j} className="flex items-center gap-2 text-xs text-gray-600 py-1 border-b border-gray-50 last:border-0">
                          <CheckCircle size={12} className="text-green-500 shrink-0"/>
                          {item}
                        </div>
                      ))}
                    </div>

                  </div>
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setBookOpen(true)}
                    className={`flex-1 ${colors.btn} text-white py-3.5 rounded-xl font-black text-sm transition-colors shadow-md hover:shadow-lg`}
                  >
                    Book {s.name} Now →
                  </button>
                  <a
                    href={`tel:${businessConfig.phone}`}
                    className="flex-1 border-2 border-gray-200 text-gray-600 py-3.5 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors text-center"
                  >
                    📞 Ask a Question First
                  </a>
                </div>

              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}